import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown"; // Import Dropdown
import { Menu } from "primereact/menu";
import { useEffect, useRef, useState, CSSProperties } from "react";
import { Toast, ToastMessage } from "primereact/toast";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { OtherUserModel } from "../../models/all-user/edit-user-model";
import ClipLoader from "react-spinners/ClipLoader";
import routeCodeServices from "../../services/route-codes/routecode.service";
import cityServices from "../../services/city/city.services";
import stateServices from "../../services/state/state.services"; // Import state service

interface DataTableProps {
  data: OtherUserModel[];
  handleAddSuccess: () => void;
}

interface ProductDetailModel {
  routeCodeID: number;
  name: string;
  description: string;
}

const CityDataTable: React.FC<DataTableProps> = (props) => {
  const menuLeft: any = useRef(null);
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [allRoutes, setAllRoutes] = useState<any>();
  const [statesList, setStatesList] = useState<any[]>([]); // Store available states
  const [showEditModal, setShowEditModal] = useState(false);
  const [loader, setLoader] = useState<boolean>();
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedRouteCode, setSelectedRouteCode] =
    useState<ProductDetailModel | null>(null);

  const override: CSSProperties = {
    display: "block",
    margin: "0 auto",
    borderColor: "red",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  useEffect(() => {
    setAllRoutes(props.data);
    fetchStates(); // Fetch states on mount
  }, [props.data]);

  const fetchStates = async () => {
    try {
      const response = await stateServices.getState(); // Fetch states list
      const states = response.map((state: any) => ({
        id: state.stateID, // Assuming the API provides stateID
        name: state.name, // Assuming the API provides the state's name
      }));
      setStatesList(states); // Update states list
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  const nameEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        type="text"
        value={options.value}
        style={{ width: "95%", border: "1px solid #090" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback!(e.target.value)
        }
      />
    );
  };

  const stateEditor = (options: ColumnEditorOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={statesList.map((state) => ({
          label: state.name,
          value: state.id,
        }))}
        onChange={(e) => options.editorCallback!(e.value)}
        placeholder="Select a State"
        style={{ width: "100%" }}
      />
    );
  };

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    let _data = [...allRoutes];
    let { newData, index } = event;
    if (_data[index] !== newData) {
      let putData = {
        cityID: newData.cityID,
        name: newData.name,
        stateID: newData.stateID,
      };

      try {
        await cityServices.updateCity(putData);
        props.handleAddSuccess();
        showMessage("Success", "City updated successfully", "success");
      } catch (error) {
        showMessage("Error", "Failed to update city", "error");
        console.error("Error updating city:", error);
      }
    }
  };

  const deleteRouteCode = async () => {
    if (!selectedRouteCode) return;

    try {
      await routeCodeServices.deleteRouteCode(selectedRouteCode.routeCodeID);
      props.handleAddSuccess();
      showMessage("Success", "City deleted successfully", "success");
    } catch (error) {
      showMessage("Error", "Failed to delete city", "error");
      console.error("Error deleting city:", error);
    } finally {
      setSelectedRouteCode(null);
      setShowDeleteDialog(false);
    }
  };

  const onHideDeleteDialog = () => {
    setSelectedRouteCode(null);
    setShowDeleteDialog(false);
  };

  const deleteTemplate = (rowData: ProductDetailModel) => {
    return (
      <Button
        icon="pi pi-trash"
        onClick={() => confirmDelete(rowData)}
        style={{
          border: "none",
          color: "gray",
          background: "none",
          borderRight: "120px",
        }}
        className="p-button-rounded"
      />
    );
  };

  const confirmDelete = (rowData: ProductDetailModel) => {
    setSelectedRouteCode(rowData);
    setShowDeleteDialog(true);
  };

  return (
    <>
      {loader ? (
        <div className="flex-align-items-center justify-content-center p-4">
          <ClipLoader
            loading={loader}
            size={50}
            aria-label="Loading Spinner"
            data-testid="loader"
            cssOverride={override}
            color="#90ee90"
          />
        </div>
      ) : (
        <>
          <Toast ref={toast} position="top-center" />
          <div className="col-12">
            <div className="card border-noround p-2">
              <DataTable
                value={allRoutes?.length > 0 ? allRoutes : allRoutes}
                editMode="row"
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                stripedRows
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                onRowEditComplete={onRowEditComplete}
              >
                <Column
                  field="name"
                  header="Name"
                  editor={nameEditor}
                  style={{ width: "42%" }}
                />
                <Column
                  field="stateID"
                  header="State"
                  editor={stateEditor}
                  style={{ width: "42%" }}
                />
                <Column
                  rowEditor
                  header="Action"
                  headerStyle={{ textAlign: "right" }}
                />
              </DataTable>
              <Menu
                popupAlignment={"left"}
                popup
                ref={menuLeft}
                id="popup_menu_left"
              />
            </div>
          </div>
          <Dialog
            visible={showEditModal}
            className="quickview w-50"
            breakpoints={{ "960px": "75vw", "641px": "90vw" }}
            modal
            onHide={() => setShowEditModal(false)}
          />
        </>
      )}
    </>
  );
};

export default CityDataTable;
